import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Product } from '../types/Product';
import type { Order, User } from '../types/User';
import { customerService } from '../services';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface UserContextType {
  user: User | null;
  orders: Order[];
  favorites: Product[];
  setUser: (user: User | null) => void;
  updateUser: (userData: Partial<User>) => void;
  addToFavorites: (product: Product) => void;
  removeFromFavorites: (productId: string) => void;
  isFavorite: (productId: string) => boolean;
  getOrder: (orderId: string) => Order | undefined;
}

const UserContext = createContext<UserContextType | undefined>(undefined);



export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [, setIsLoadingUser] = useState(true);
  const [, setIsLoadingOrders] = useState(false);
  const [, setIsLoadingFavorites] = useState(false);
  const { user: authUser, isAuthenticated } = useAuth();
  const { showToast } = useToast();

  // Load user data when authenticated
  useEffect(() => {
    const loadUserData = async () => {
      if (!isAuthenticated || !authUser) {
        setUser(null);
        setOrders([]);
        setFavorites([]);
        setIsLoadingUser(false);
        return;
      }

      try {
        setIsLoadingUser(true);
        
        // Load user details
        const userResponse = await customerService.getCurrentCustomer();
        if (userResponse.success && userResponse.data) {
          setUser(userResponse.data);
          
          // Load orders
          setIsLoadingOrders(true);
          const ordersResponse = await customerService.getCustomerOrders(userResponse.data.id);
          if (ordersResponse.success) {
            setOrders(ordersResponse.data);
          }
          setIsLoadingOrders(false);
          
          // Load favorites/wishlist
          setIsLoadingFavorites(true);
          const favoritesResponse = await customerService.getWishlist(userResponse.data.id);
          if (favoritesResponse.success) {
            setFavorites(favoritesResponse.data);
          }
          setIsLoadingFavorites(false);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        showToast('Failed to load user data', 'error');
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadUserData();
  }, [isAuthenticated, authUser]);


  const addToFavorites = async (product: Product) => {
    if (!user) {
      showToast('Please login to add favorites', 'error');
      return;
    }

    try {
      const response = await customerService.addToWishlist(user.id, product.id);
      if (response.success) {
        setFavorites(prev => {
          if (prev.some(p => p.id === product.id)) return prev;
          return [...prev, product];
        });
        showToast('Added to favorites', 'success');
      } else {
        showToast(response.error || 'Failed to add to favorites', 'error');
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
      showToast('Failed to add to favorites', 'error');
    }
  };

  const removeFromFavorites = async (productId: string) => {
    if (!user) return;

    try {
      const response = await customerService.removeFromWishlist(user.id, productId);
      if (response.success) {
        setFavorites(prev => prev.filter(p => p.id !== productId));
        showToast('Removed from favorites', 'success');
      } else {
        showToast(response.error || 'Failed to remove from favorites', 'error');
      }
    } catch (error) {
      console.error('Error removing from favorites:', error);
      showToast('Failed to remove from favorites', 'error');
    }
  };

  const isFavorite = (productId: string) => {
    return favorites.some(p => p.id === productId);
  };

  const updateUser = async (userData: Partial<User>) => {
    if (!user) return;

    try {
      const response = await customerService.updateCustomer(user.id, userData);
      if (response.success && response.data) {
        setUser(response.data);
        showToast('Profile updated successfully', 'success');
      } else {
        showToast(response.error || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showToast('Failed to update profile', 'error');
    }
  };

  const getOrder = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        orders,
        favorites,
        setUser,
        updateUser,
        addToFavorites,
        removeFromFavorites,
        isFavorite,
        getOrder,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};